import { safeGetItem, safeSetItem } from "@/lib/safe-storage";
import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Check, Copy, KeyRound, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  LAST_CODE_STORAGE_KEY,
  PIN_LENGTH,
  codeToEmail,
  digitsOnly,
  generateAccessCode,
  isValidAccessCode,
  isValidPin,
  normalizeAccessCode,
} from "@/lib/access-code";
import { signInWithAccessCode } from "@/lib/auth-signin.functions";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in — MyPriorities" },
      {
        name: "description",
        content:
          "Sign in to your MyPriorities workspace with your access code and PIN — priorities, tasks, CRM, contracts and reporting in one place.",
      },
      { property: "og:title", content: "Sign in — MyPriorities" },
      {
        property: "og:description",
        content: "Access code and PIN sign-in for your MyPriorities workspace.",
      },

      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthRoute,
});

function AuthRoute() {
  return (
    <SectionErrorBoundary label="sign in">
      <AuthPage />
    </SectionErrorBoundary>
  );
}

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [busy, setBusy] = useState(false);
  const signIn6Digit = useServerFn(signInWithAccessCode);

  // sign in
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");

  // sign up
  const [newCode, setNewCode] = useState(() => generateAccessCode());
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [savedCode, setSavedCode] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && data.session) void navigate({ to: "/overview", replace: true });
    });
    const stored = safeGetItem(LAST_CODE_STORAGE_KEY);
    if (stored) setCode(stored);
    return () => {
      active = false;
    };
  }, [navigate]);

  async function openHubAfterSignIn() {
    // signInWithPassword can emit SIGNED_IN before every browser has finished
    // committing the session to storage. Confirm that handoff before running
    // the protected route guard, which avoids Safari/Firefox redirect loops.
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session) {
      throw new Error("Signed in, but the session could not be saved. Please try again.");
    }
    await navigate({ to: "/overview", replace: true });
  }

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(newCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please write the code down.");
    }
  }

  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    const canonical = normalizeAccessCode(code);
    if (!isValidAccessCode(canonical)) {
      toast.error("That access code doesn't look right.");
      return;
    }
    setBusy(true);
    try {
      // Sign-in runs server-side so repeated wrong PINs are counted and locked
      // out somewhere the browser can't reset.
      const result = await signIn6Digit({ data: { code: canonical, pin } });
      if (!result.ok) {
        if (result.retryAfterSeconds > 0) {
          const minutes = Math.ceil(result.retryAfterSeconds / 60);
          throw new Error(
            `Too many attempts. Try again in ${minutes} minute${minutes === 1 ? "" : "s"}.`,
          );
        }
        throw new Error("Access code and PIN don't match.");
      }
      const { error } = await supabase.auth.setSession({
        access_token: result.accessToken,
        refresh_token: result.refreshToken,
      });
      if (error) throw new Error("Signed in, but the session could not be saved. Please try again.");
      safeSetItem(LAST_CODE_STORAGE_KEY, canonical);
      await openHubAfterSignIn();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  async function signUp(e: React.FormEvent) {
    e.preventDefault();
    if (newPin !== confirmPin) {
      toast.error("The two PINs don't match.");
      return;
    }
    setBusy(true);
    try {
      // auth.users enforces one account per address, so a collision just means
      // trying again with a freshly generated code.
      let candidate = newCode;
      for (let attempt = 0; attempt < 5; attempt++) {
        const { data, error } = await supabase.auth.signUp({
          email: codeToEmail(candidate),
          password: newPin,
          options: { data: { access_code: candidate } },
        });
        if (!error) {
          setNewCode(candidate);
          safeSetItem(LAST_CODE_STORAGE_KEY, candidate);
          if (!data.session) {
            const { error: signInError } = await supabase.auth.signInWithPassword({
              email: codeToEmail(candidate),
              password: newPin,
            });
            if (signInError) throw signInError;
          }
          await openHubAfterSignIn();
          return;
        }
        const taken = /already|registered|exists/i.test(error.message);
        if (!taken) throw error;
        candidate = generateAccessCode();
      }
      throw new Error("Couldn't generate a free access code. Please try again.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  const signUpReady = savedCode && isValidPin(newPin) && isValidPin(confirmPin);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="panel w-full max-w-sm p-8">
        <Link to="/" className="font-display text-sm font-bold">
          <span className="text-primary">◆</span> MyPriorities
        </Link>

        {mode === "signin" ? (
          <>
            <h1 className="mt-6 text-2xl font-bold">Welcome back</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter your access code and PIN.
            </p>

            <form onSubmit={signIn} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Access code</Label>
                <Input
                  id="code"
                  autoCapitalize="characters"
                  autoComplete="username"
                  placeholder="SGL-XXXXXX"
                  className="font-mono tracking-widest"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pin">PIN</Label>
                <Input
                  id="pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="current-password"
                  placeholder={"•".repeat(PIN_LENGTH)}
                  className="font-mono tracking-[0.4em]"
                  value={pin}
                  onChange={(e) => setPin(digitsOnly(e.target.value))}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={busy || !isValidPin(pin) || !isValidAccessCode(code)}
              >
                <KeyRound className="size-4" /> Sign in
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="mt-6 text-2xl font-bold">Create your workspace</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              No email needed — save your access code and pick a {PIN_LENGTH}-digit PIN.
            </p>

            <div className="mt-6 rounded-md border border-primary/40 bg-primary/5 p-4 text-center">
              <p className="label-caps">Your access code</p>
              <p className="mt-2 font-mono text-2xl font-bold tracking-[0.2em] text-primary">
                {newCode}
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={copyCode}>
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                {copied ? "Copied" : "Copy code"}
              </Button>
            </div>

            <div className="mt-4 flex gap-2 rounded-md border border-border bg-surface p-3 text-xs text-muted-foreground">
              <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warning" />
              <p>
                There's no email on this account, so there's no reset link. If you lose both
                the code and the PIN, the workspace can't be recovered.
              </p>
            </div>

            <form onSubmit={signUp} className="mt-4 space-y-4">
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={savedCode}
                  onCheckedChange={(v) => setSavedCode(Boolean(v))}
                  className="mt-0.5"
                />
                I've saved my access code somewhere safe
              </label>

              <div className="space-y-2">
                <Label htmlFor="new-pin">Choose a PIN</Label>
                <Input
                  id="new-pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  placeholder={"•".repeat(PIN_LENGTH)}
                  className="font-mono tracking-[0.4em]"
                  value={newPin}
                  onChange={(e) => setNewPin(digitsOnly(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-pin">Confirm PIN</Label>
                <Input
                  id="confirm-pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  placeholder={"•".repeat(PIN_LENGTH)}
                  className="font-mono tracking-[0.4em]"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(digitsOnly(e.target.value))}
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy || !signUpReady}>
                Create my workspace
              </Button>
            </form>
          </>
        )}

        <button
          type="button"
          onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
          className="mt-6 w-full text-center text-sm text-muted-foreground hover:text-foreground"
        >
          {mode === "signin" ? "No workspace yet? Create one" : "Already have a code? Sign in"}
        </button>
      </div>
    </div>
  );
}
